import { createAction } from '@reduxjs/toolkit';
import { BroadcastWSActions, BroadcastWSMessages } from 'common/constants';
import {
  AgoraCloudPlayerInfo,
  BroadcastConnectionInfo,
  BroadcastDemoteSpeakerApiPayload,
  BroadcastSpeakerApiPayload,
  HandleStageInvitationParams,
  ScreenshareDetailsApiPayload,
  StageInvitationBasicInfo,
} from 'domain/Broadcast';
import { EventHostType, MeetingGuest, MeetingGuestApiPayload } from 'domain/event';
import i18n from 'i18n';
import { addBroadcastInfoMessage, removeAppMessage } from 'modules/alerts/state';
import { createMeetingWSAction, createMeetingWSMessage } from 'store/utils';

import {
  BroadcastDemoteUserParams,
  MeetingWSParams,
  SendBroadcastStageInvitationPayload,
  StageInvitationAcceptedPayload,
  StageInvitationRejectedPayload,
  StageInvitationSentPayload,
  StartBroadcastPayload,
  StartNatteringPayload,
  StartScreensharingPayload,
  ToggleChatModerationParams,
} from '../types';

export const broadcastStarted_WS = createMeetingWSMessage<BroadcastConnectionInfo>(BroadcastWSMessages.Started);
export const broadcastStopped_WS = createMeetingWSMessage(BroadcastWSMessages.Stopped);
export const stageInvitationReceived_WS = createMeetingWSMessage<StageInvitationBasicInfo>(
  BroadcastWSMessages.StageInvitationReceived
);
export const stageInvitationAccepted_WS = createMeetingWSMessage<StageInvitationAcceptedPayload>(
  BroadcastWSMessages.StageInvitationAccepted
);
export const stageInvitationSent_WS = createMeetingWSMessage<StageInvitationSentPayload>(
  BroadcastWSMessages.StageInvitationSent
);
export const stageInvitationRejected_WS = createMeetingWSMessage<StageInvitationRejectedPayload>(
  BroadcastWSMessages.StageInvitationRejected
);
export const broadcastVideoViaUrlPlayed_WS = createMeetingWSMessage<AgoraCloudPlayerInfo>(
  BroadcastWSMessages.VideoViaUrlPlayed
);
export const broadcastVideoViaUrlStopped_WS = createMeetingWSMessage<{ message?: string; callerUserId: number }>(
  BroadcastWSMessages.VideoViaUrlStopped
);
export const broadcastVideoViaUrlPaused_WS = createMeetingWSMessage(BroadcastWSMessages.VideoViaUrlPaused);
export const broadcastVideoViaUrlResumed_WS = createMeetingWSMessage(BroadcastWSMessages.VideoViaUrlResumed);
export const screensharingStarted_WS = createMeetingWSMessage<ScreenshareDetailsApiPayload>(
  BroadcastWSMessages.ScreensharingStarted
);
export const screensharingStopped_WS = createMeetingWSMessage(BroadcastWSMessages.ScreensharingStopped);
export const audienceJoined_WS = createMeetingWSMessage<MeetingGuestApiPayload>(BroadcastWSMessages.AudienceJoined);
export const audienceJoinedList_WS = createMeetingWSMessage<MeetingGuestApiPayload[]>(
  BroadcastWSMessages.AudienceJoinedList
);
export const broadcastDemoted_WS = createMeetingWSMessage(BroadcastWSMessages.Demoted);
export const broadcastGuestDemoted_WS = createMeetingWSMessage<BroadcastDemoteSpeakerApiPayload>(
  BroadcastWSMessages.GuestDemoted
);
export const broadcastPromoted_WS = createMeetingWSMessage<{ publisherAccessToken: string }>(
  BroadcastWSMessages.Promoted
);
export const broadcastGuestPromoted_WS = createMeetingWSMessage<BroadcastSpeakerApiPayload>(
  BroadcastWSMessages.GuestPromoted
);
export const broadcastGuestPromotedList_WS = createMeetingWSMessage<BroadcastSpeakerApiPayload[]>(
  BroadcastWSMessages.GuestPromotedList
);
export const broadcastChatModerationToggled_WS = createMeetingWSMessage<ToggleChatModerationParams>(
  BroadcastWSMessages.ChatModerationToggled
);

export const startNattering_WS = createMeetingWSAction<MeetingWSParams<StartNatteringPayload>>(
  BroadcastWSActions.StartNattering
);
export const startBroadcast_WS = createMeetingWSAction<MeetingWSParams<StartBroadcastPayload>>(
  BroadcastWSActions.Start
);
export const broadcastPromoteOwner_WS = createMeetingWSAction<MeetingWSParams>(BroadcastWSActions.PromoteCo);
export const broadcastDemoteUser_WS = createMeetingWSAction<MeetingWSParams<BroadcastDemoteUserParams>>(
  BroadcastWSActions.Demote
);
export const sendBroadcastStageInvitation_WS = createMeetingWSAction<
  MeetingWSParams<SendBroadcastStageInvitationPayload>
>(BroadcastWSActions.SendStageInvitation);
export const acceptBroadcastStageInvitation_WS = createMeetingWSAction<MeetingWSParams<HandleStageInvitationParams>>(
  BroadcastWSActions.AcceptStageInvitation
);
export const rejectBroadcastStageInvitation_WS = createMeetingWSAction<MeetingWSParams<HandleStageInvitationParams>>(
  BroadcastWSActions.RejectStageInvitation
);
export const startScreensharing_WS = createMeetingWSAction<MeetingWSParams<StartScreensharingPayload>>(
  BroadcastWSActions.StartSharing
);
export const stopScreensharing_WS = createMeetingWSAction<MeetingWSParams>(BroadcastWSActions.StopSharing);

export const startRecording_WS = createMeetingWSAction(BroadcastWSActions.StartRecording);
export const stopRecording_WS = createMeetingWSAction(BroadcastWSActions.StopRecording);
export const recordingStarted_WS = createMeetingWSMessage(BroadcastWSMessages.RecordingStarted);
export const recordingStopped_WS = createMeetingWSMessage(BroadcastWSMessages.RecordingStopped);
export const rateNatterExperienceRequested = createAction('[Broadcast] Rate Natter Experience requested');
export const dismissRateNatterExperienceAfterTimeout = createAction(
  '[Broadcast] Dismiss Rate Natter Experience after timeout'
);

export const broadcastActions = {
  broadcastStopped: createAction('[Event Broadcast] broadcast stopped'),
  natteringStarted: createAction('[Event Broadcast] nattering started'),
  backstageStarted: createAction<{ hosts: EventHostType[] }>('[Event Broadcast] backstage started'),
  promotedToSpeaker: createAction<MeetingGuest>('[Event Broadcast] promoted to speaker'),
  startScreenshare: createAction('[Event Broadcast] Screensharing started'),
  stopScreenshare: createAction('[Event Broadcast] Screensharing stopped'),
  /** The floating-window toast's action; `meeting.listener` opens the window from it. */
  floatingWindowRequested: createAction('[Event Broadcast] floating window requested'),
};

export const backstageActions = {
  viewRequestsClicked: createAction('[Backstage] view requests clicked'),
};

const FLOATING_WINDOW_ALERT_ID = 'screen-share-floating-window';

/**
 * Offered when the browser refuses to open the screen-share floating window on its own (Firefox
 * does not count approving the share picker as a click). The toast's click is the user activation
 * `requestWindow` needs, and it lasts a few seconds, far longer than the listener takes. The
 * Present menu carries the same action, as a toast must never be the only way to reach it.
 */
export const offerFloatingWindow = () =>
  addBroadcastInfoMessage({
    id: FLOATING_WINDOW_ALERT_ID,
    message: i18n.t('event:broadcast.screenSharePip.blockedMessage'),
    actionLabel: i18n.t('event:broadcast.screenSharePip.openWindow'),
    action: broadcastActions.floatingWindowRequested(),
  });

export const dismissFloatingWindowOffer = () => removeAppMessage(FLOATING_WINDOW_ALERT_ID);

export const addSupportRequestedMessage = addBroadcastInfoMessage({
  message: 'Participants are requesting help.',
  actionLabel: 'VIEW',
  action: backstageActions.viewRequestsClicked(),
  timeout: 1_000_000,
});
